import { useEffect, useMemo, useRef, useState } from 'react';
import { controlerDateSaisie } from '../../../shared/business-logic/semaines.ts';
import {
  validerCpn, normaliserIndicateurs, CPN_INDICATEURS, CPN_INDICATEUR_LABELS, CPN_IMPLICATIONS,
} from '../../../shared/business-logic/index.ts';
import type { CpnIndicateur } from '../../../shared/business-logic/cpn.ts';
import { getSession } from '../api/client.ts';
import {
  enregistrerCpn, enregistrerModificationCpn, rafraichirCacheCpn, modifierCpnLocale, ecouterEtatSynchroCpn, supprimerCpn,
} from '../offline/syncQueueCpn.ts';
import { chargerCpnCache, listerParPeriode, enregistrerTotauxCpnLocaux } from '../offline/dbCpn.ts';
import { fusionnerSaisiesCpn, saisiesCpnComptees, totauxCpnLocaux } from '../../../shared/business-logic/calculLocal.ts';
import type { CpnAffichee, CpnElementFile, CpnServeurLigne } from '../../../shared/business-logic/calculLocal.ts';
import ImportCpn from '../components/ImportCpn.tsx';
import { AFFICHAGE } from '../config/affichage.ts';
import { TotauxCpnEnDirect } from '../components/TotauxEnDirect.tsx';
import type { Periode } from './Accueil.tsx';

// Regroupement à l'écran uniquement (affichage) — les libellés/valeurs
// viennent tous de shared/business-logic/cpn.ts, source unique de vérité.
const BLOCS: Array<{ titre: string; cles: CpnIndicateur[] }> = [
  { titre: 'A — CPN1', cles: ['cpn1', 'cpnPrecoce'] },
  { titre: 'B — CPN, MMS, paludisme', cles: ['cpn4', 'cpn5Plus', 'mms', 'palu'] },
  { titre: 'C — TPI et MID', cles: ['tpi1', 'tpi2', 'tpi3', 'tpi4Plus', 'mid'] },
  { titre: 'D — HTA et diabète', cles: ['hta', 'diabeteDepistage', 'diabetique'] },
  { titre: 'E — Syphilis et VIH', cles: ['syphilisTest', 'syphilisPositif', 'vihTest', 'vihPositif'] },
  { titre: 'F — Références', cles: ['refAc', 'refSup'] },
  { titre: 'G — Td', cles: ['td1', 'td2Plus'] },
];
const PAQUET_PREMIERE_CPN: CpnIndicateur[] = ['cpn1', 'mms', 'syphilisTest', 'vihTest'];

interface CpnForm {
  numeroRegistre: string;
  ageAns: string;
  indicateurs: Record<CpnIndicateur, boolean>;
  datePrestation: string;
  /** 'oui' | 'non' | '' (non renseigné) */
  htaDepistage: 'oui' | 'non' | '';
  numeroRceLie: string;
}
const aujourdhui = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const cleDate = (periodeId: string) => `prerma_derniere_date_cpn_${periodeId}`;
function dateParDefaut(p: { id: string; annee: number; mois: number }): string {
  const memo = localStorage.getItem(cleDate(p.id));
  if (memo && controlerDateSaisie(memo, p, aujourdhui()) === null) return memo;
  const t = aujourdhui();
  return controlerDateSaisie(t, p, t) === null ? t : '';
}
const formVide = (): CpnForm => ({ numeroRegistre: '', ageAns: '', indicateurs: normaliserIndicateurs(null), datePrestation: '', htaDepistage: '', numeroRceLie: '' });

type CpnEnregistree = CpnAffichee;

/** Entier strict : « 22 » → 22 ; « 22,5 », « 1e1 », « abc » → NaN (refusé par la validation, jamais arrondi ni deviné) ; vide → null. */
function entierStrict(texte: string): number | null {
  const t = texte.trim();
  if (t === '') return null;
  return /^\d+$/.test(t) ? Number(t) : NaN;
}

export default function SaisieCpn({ periode }: { periode: Periode | null }) {
  const [form, setForm] = useState<CpnForm>(formVide());
  const [envoi, setEnvoi] = useState<{ ok: boolean; message: string } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [serveur, setServeur] = useState<CpnServeurLigne[]>([]);
  const [file, setFile] = useState<CpnElementFile[]>([]);
  const [listeChargee, setListeChargee] = useState(false); // les totaux ne sont rangés dans IndexedDB qu'une fois la liste réellement chargée
  const [edition, setEdition] = useState<{ id?: string; localId?: string; version?: number } | null>(null);
  const [garderCases, setGarderCases] = useState(false);
  const registreInputRef = useRef<HTMLInputElement>(null);
  const ageInputRef = useRef<HTMLInputElement>(null);

  const chargementId = useRef(0); // ignore les réponses de rechargement périmées

  useEffect(() => {
    setListeChargee(false);
    if (periode) void chargerListe(periode.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode?.id]);
  // La liste se met à jour toute seule quand la synchronisation avance (envoi terminé…).
  useEffect(() => ecouterEtatSynchroCpn((etat) => { if (periode && etat === 'IDLE') void chargerListe(periode.id); }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [periode?.id]);

  async function chargerListe(periodeId: string) {
    const id = ++chargementId.current;
    let rows: unknown[];
    try { rows = await rafraichirCacheCpn(periodeId); } catch { rows = await chargerCpnCache(periodeId); }
    const enFile = (await listerParPeriode(periodeId)) as unknown as CpnElementFile[];
    if (id !== chargementId.current) return;
    // Saisies dont la suppression est en file : masquées même si le serveur (ou un ancien cache) les renvoie encore.
    const enSuppression = new Set(enFile.filter((i) => i.operation === 'DELETE' && i.syncStatus === 'PENDING').map((i) => i.cpnId));
    setServeur((rows as unknown as CpnServeurLigne[]).filter((r) => !enSuppression.has(r.id)));
    setFile(enFile);
    setListeChargee(true);
  }

  // Liste = état serveur (ou cache) + file locale : inclut donc les femmes saisies hors connexion.
  const liste: CpnEnregistree[] = useMemo(() => fusionnerSaisiesCpn(serveur, file), [serveur, file]);

  // TOTAUX LOCAUX : recalculés dès que la liste change, sans réseau ; même moteur que le serveur et que l'export.
  const comptees = useMemo(() => saisiesCpnComptees(liste), [liste]);
  const totaux = useMemo(() => totauxCpnLocaux(comptees), [comptees]);
  useEffect(() => {
    if (!periode || !listeChargee) return;
    enregistrerTotauxCpnLocaux(periode.id, getSession().csbId ?? '', totaux as unknown as Record<string, unknown>).catch(() => { /* stockage indisponible : l'écran reste exact */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totaux, periode?.id, listeChargee]);

  const prochainRegistre = useMemo(() => {
    const nums = liste.map((l) => l.numeroRegistre).filter((n) => Number.isFinite(n) && n > 0);
    return nums.length === 0 ? '' : String(Math.max(...nums) + 1);
  }, [liste]);

  const listeAffichee = useMemo(() => [...liste].reverse(), [liste]);

  const erreurDate = useMemo(() => (periode ? controlerDateSaisie(form.datePrestation, periode, aujourdhui()) : 'Aucune période.'), [form.datePrestation, periode]);
  const erreurLien: string | null = form.numeroRceLie !== '' && !/^[0-9]{1,9}$/.test(form.numeroRceLie) ? 'N° RCE : chiffres uniquement.' : null;
  useEffect(() => {
    if (periode && !edition) setForm((f) => (f.datePrestation ? f : { ...f, datePrestation: dateParDefaut(periode) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode?.id]);
  const ageAnsNombre = entierStrict(form.ageAns);
  const registreNombre = entierStrict(form.numeroRegistre);
  // Un N° de registre déjà utilisé (sur le serveur OU en attente sur cet appareil) serait refusé plus tard (409) : on le bloque tout de suite.
  const doublonRegistre = registreNombre !== null && Number.isFinite(registreNombre)
    && liste.some((l) => l.numeroRegistre === registreNombre && !(edition && ((edition.id && edition.id === l.id) || (edition.localId && edition.localId === l.localId))));
  const validation = useMemo(() => {
    const v = validerCpn({ ageAns: ageAnsNombre, numeroRegistre: registreNombre });
    return doublonRegistre ? { ok: false, erreurs: [...v.erreurs, `Le N° de registre ${registreNombre} existe déjà pour cette période.`] } : v;
  }, [ageAnsNombre, registreNombre, doublonRegistre]);
  const auMoinsUneCase = CPN_INDICATEURS.some((c) => form.indicateurs[c]);

  function basculerIndicateur(cle: CpnIndicateur) {
    setForm((f) => {
      const actif = !f.indicateurs[cle];
      const indicateurs = { ...f.indicateurs, [cle]: actif };
      if (actif && CPN_IMPLICATIONS[cle]) {
        indicateurs[CPN_IMPLICATIONS[cle]!] = true;
      }
      if (!actif) {
        for (const [source, cible] of Object.entries(CPN_IMPLICATIONS)) {
          if (cible === cle) indicateurs[source as CpnIndicateur] = false;
        }
      }
      return { ...f, indicateurs };
    });
  }

  function appliquerPaquet() {
    setForm((f) => {
      const indicateurs = { ...f.indicateurs };
      for (const cle of PAQUET_PREMIERE_CPN) indicateurs[cle] = true;
      return { ...f, indicateurs };
    });
  }

  function reinitialiserFormulaire(registreSuggere: string) {
    setForm({ numeroRegistre: registreSuggere, ageAns: '', indicateurs: garderCases ? form.indicateurs : normaliserIndicateurs(null), datePrestation: form.datePrestation, htaDepistage: '', numeroRceLie: '' });
  }

  function ouvrirPourEdition(c: CpnEnregistree) {
    setEdition({ id: c.id, localId: c.localId, version: c.version });
    setForm({ numeroRegistre: String(c.numeroRegistre), ageAns: String(c.ageAns), indicateurs: { ...c.indicateurs }, datePrestation: c.datePrestation ?? '', htaDepistage: c.htaDepistage === true ? 'oui' : c.htaDepistage === false ? 'non' : '', numeroRceLie: c.numeroRceLie ?? '' });
    setEnvoi(null);
  }

  function annulerEdition() {
    setEdition(null);
    setForm({ ...formVide(), numeroRegistre: prochainRegistre, datePrestation: periode ? dateParDefaut(periode) : '' });
    setEnvoi(null);
  }

  async function supprimer(c: CpnEnregistree) {
    if (!periode) return;
    const attente = !c.id ? ' (pas encore envoyée : elle sera simplement retirée de cet appareil)' : '';
    if (!window.confirm(`Supprimer la saisie CPN N° ${c.numeroRegistre} (âge ${c.ageAns} ans) ?${attente}\n\nLes totaux seront recalculés tout de suite.`)) return;
    setEnvoi(null);
    try {
      const session = getSession();
      await supprimerCpn({ id: c.id, localId: c.localId, version: c.version, numeroRegistre: c.numeroRegistre }, periode.id, session.csbId ?? '');
      // Si la saisie supprimée était en cours de modification, on quitte le mode édition.
      if (edition && ((edition.id && edition.id === c.id) || (edition.localId && edition.localId === c.localId))) {
        setEdition(null);
        setForm({ ...formVide(), datePrestation: periode ? dateParDefaut(periode) : '' });
      }
      setEnvoi({ ok: true, message: `Supprimé : N° ${c.numeroRegistre}${navigator.onLine ? '' : ' (sera confirmé au retour du réseau)'}` });
      await chargerListe(periode.id);
    } catch (err: any) {
      setEnvoi({ ok: false, message: err.message ?? 'Erreur de suppression locale' });
    }
  }

  async function enregistrer() {
    if (!validation.ok || !periode) return;
    setEnCours(true);
    setEnvoi(null);
    try {
      const session = getSession();
      const payload = {
        periodeId: periode.id, numeroRegistre: registreNombre, ageAns: ageAnsNombre, indicateurs: form.indicateurs,
        datePrestation: form.datePrestation, htaDepistage: form.htaDepistage === 'oui' ? true : form.htaDepistage === 'non' ? false : null, numeroRceLie: form.numeroRceLie.trim() || null,
      };
      localStorage.setItem(cleDate(periode.id), form.datePrestation);   // saisie en série : même date proposée à la saisie suivante
      if (edition) {
        if (edition.id) await enregistrerModificationCpn(periode.id, session.csbId ?? '', edition.id, edition.version ?? 1, payload);
        else if (edition.localId) await modifierCpnLocale(edition.localId, periode.id, session.csbId ?? '', payload);
        setEnvoi({ ok: true, message: `Modifié : N° ${registreNombre}` });
        setEdition(null);
        reinitialiserFormulaire(prochainRegistre);
      } else {
        await enregistrerCpn(periode.id, session.csbId ?? '', payload);
        setEnvoi({ ok: true, message: `Enregistré : N° ${registreNombre}` });
        reinitialiserFormulaire(String((registreNombre ?? 0) + 1));
      }
      await chargerListe(periode.id);
      ageInputRef.current?.focus();
    } catch (err: any) {
      setEnvoi({ ok: false, message: err.message ?? "Erreur d'enregistrement local" });
    } finally {
      setEnCours(false);
    }
  }

  function toucheAge(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && auMoinsUneCase) {
      e.preventDefault();
      enregistrer();
    }
  }
  function toucheRegistre(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      ageInputRef.current?.focus();
    }
  }

  if (!periode) {
    return <p>Sélectionnez d'abord une période sur l'écran Accueil.</p>;
  }
  if (periode.statut === 'CLOTURE') {
    return <p>Cette période est clôturée : lecture seule. Contactez l'administrateur pour la rouvrir si nécessaire.</p>;
  }

  return (
    <div className="module-cpn">
      <h1>{edition ? 'Modifier une saisie CPN' : 'Saisie CPN'}</h1>
      {edition && (
        <p>
          Modification de la saisie N° <strong>{form.numeroRegistre}</strong>.{' '}
          <button type="button" className="secondaire" onClick={annulerEdition}>Annuler</button>
        </p>
      )}
      {!edition && (
        <div className="counter-bar">
          <span>Femmes saisies ce mois-ci</span>
          <b>{liste.length}</b>
        </div>
      )}
      {!edition && listeChargee && <TotauxCpnEnDirect totaux={totaux} refuses={liste.length - comptees.length} />}
      {!edition && AFFICHAGE.importCpn && (
        <ImportCpn
          periodeId={periode.id}
          csbId={getSession().csbId ?? ''}
          existants={liste.map((l) => l.numeroRegistre)}
          onImporte={() => chargerListe(periode.id)}
        />
      )}

      <label htmlFor="cpn-date">Date de prestation *</label>
      <input id="cpn-date" type="date" value={form.datePrestation} required
        min={periode ? `${periode.annee}-${String(periode.mois).padStart(2, '0')}-01` : undefined}
        onChange={(e) => setForm({ ...form, datePrestation: e.target.value })} />
      {erreurDate && (form.datePrestation !== '' || !!edition) && <p className="erreur" role="alert">{erreurDate}</p>}

      <label htmlFor="cpn-registre">N° registre CPN *</label>
      <input
        id="cpn-registre" ref={registreInputRef} inputMode="numeric" value={form.numeroRegistre}
        placeholder="ex: 145" onKeyDown={toucheRegistre}
        onChange={(e) => setForm({ ...form, numeroRegistre: e.target.value })}
      />

      <label htmlFor="cpn-age">Âge (ans) *</label>
      <input
        id="cpn-age" ref={ageInputRef} inputMode="numeric" value={form.ageAns}
        placeholder="ex: 22" onKeyDown={toucheAge}
        onChange={(e) => setForm({ ...form, ageAns: e.target.value })}
      />

      <label htmlFor="cpn-hta-dep">Dépistage HTA (tension prise) <span className="aide">— pour le rapport MNT/MTN</span></label>
      <select id="cpn-hta-dep" value={form.htaDepistage} onChange={(e) => setForm({ ...form, htaDepistage: e.target.value as CpnForm['htaDepistage'] })}>
        <option value="">Non renseigné</option>
        <option value="oui">Oui — dépistage réalisé</option>
        <option value="non">Non — pas de dépistage</option>
      </select>
      {form.indicateurs.hta && form.htaDepistage === 'non' && <p className="erreur" role="alert">HTA diagnostiquée : le dépistage ne peut pas être « Non ».</p>}

      <label htmlFor="cpn-rce-lie">N° RCE de la même personne <span className="aide">(facultatif — si elle a aussi été vue en consultation ce jour-là : évite le double comptage HTA / diabète)</span></label>
      <input id="cpn-rce-lie" inputMode="numeric" value={form.numeroRceLie} placeholder="ex: 57" onChange={(e) => setForm({ ...form, numeroRceLie: e.target.value.replace(/\D/g, '').slice(0, 9) })} />

      <div style={{ margin: '10px 0' }}>
        <button type="button" className="secondaire" onClick={appliquerPaquet}>
          ⚡ Paquet 1ère CPN (CPN1, MMS, test syphilis, test VIH)
        </button>
      </div>

      {BLOCS.map((bloc) => (
        <div key={bloc.titre} style={{ marginTop: 12 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 700, marginBottom: 4 }}>{bloc.titre}</div>
          <div className="chip-grid">
            {bloc.cles.map((cle) => (
              <div
                key={cle}
                className={'chip' + (form.indicateurs[cle] ? ' selected' : '')}
                onClick={() => basculerIndicateur(cle)}
              >
                {CPN_INDICATEUR_LABELS[cle].label}
              </div>
            ))}
          </div>
        </div>
      ))}

      {!validation.ok && (form.numeroRegistre || form.ageAns) && (
        <div className="messages-erreur">
          <strong>Contrôle de saisie :</strong>
          <ul>{validation.erreurs.map((e, i) => <li key={i}>{e}</li>)}</ul>
        </div>
      )}

      <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={enregistrer} disabled={!validation.ok || !!erreurDate || !!erreurLien || (form.indicateurs.hta && form.htaDepistage === 'non') || !form.numeroRegistre || !form.ageAns || enCours}>
          {enCours ? 'Enregistrement…' : edition ? 'Enregistrer la modification' : 'Enregistrer et continuer'}
        </button>
        {!edition && (
          <label style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={garderCases} onChange={(e) => setGarderCases(e.target.checked)} />
            Garder les cases cochées après ajout (saisie en série)
          </label>
        )}
      </div>
      {envoi && <p className={envoi.ok ? '' : 'messages-erreur'}>{envoi.message}</p>}

      {!edition && liste.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h2>Femmes déjà saisies pour cette période</h2>
          <table>
            <thead><tr><th>N° registre</th><th>Âge</th><th>Cochés</th><th>État</th><th>Actions</th></tr></thead>
            <tbody>
              {listeAffichee.map((c) => (
                <tr key={c.cle}>
                  <td>{c.numeroRegistre}</td>
                  <td>{c.ageAns}</td>
                  <td style={{ textAlign: 'left', fontSize: '0.8rem' }}>
                    {CPN_INDICATEURS.filter((cle) => c.indicateurs[cle]).map((cle) => CPN_INDICATEUR_LABELS[cle].court).join(', ') || '—'}
                  </td>
                  <td>
                    {c.statut === 'ATTENTE' && 'En attente d\'envoi'}
                    {c.statut === 'ERREUR' && 'Refusé'}
                    {c.statut === 'CONFLIT' && 'Conflit'}
                    {!c.statut && 'Enregistré'}
                    {c.message && <small className="note" style={{ display: 'block' }}>{c.message}</small>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button type="button" className="icone-action" title={`Modifier la saisie N° ${c.numeroRegistre}`} aria-label={`Modifier la saisie N° ${c.numeroRegistre}`} onClick={() => ouvrirPourEdition(c)}>✏️</button>{' '}
                    <button type="button" className="icone-action danger" title={`Supprimer la saisie N° ${c.numeroRegistre}`} aria-label={`Supprimer la saisie N° ${c.numeroRegistre}`} onClick={() => void supprimer(c)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
